<template>
  <div>
    <h4 class="color-grey">
      Operations ({{ operationsCount }}/{{ operationsLimit }})
    </h4>
    <ak-integration-s-f-t-p-operations-list
      :data="service.inboundOperationsTableData.value"
      :can-create-operation="canCreateOperation"
      @on-create-operation="createOperationHandler"
      @on-update-operation="updateOperationHandler"
      @on-delete-operation="deleteOperationHandler"
    />
    <ak-integration-s-f-t-p-operation :service="operationsService" />
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import useSFTPOperation from './useSFTPOperation'

import IntegrationSFTPOperation from './IntegrationSFTPOperation.vue'
import IntegrationSFTPOperationsList from './IntegrationSFTPOperationsList.vue'

export default defineComponent({
  name: 'AkIntegrationSFTPOperations',
  components: {
    [IntegrationSFTPOperation.name]: IntegrationSFTPOperation,
    [IntegrationSFTPOperationsList.name]: IntegrationSFTPOperationsList,
  },
  props: {
    service: {
      required: true,
      type: Object,
    },
  },
  setup(props) {
    const operationsService = useSFTPOperation(props.service)

    /**
     * Create a new operation of the given type
     * @param {String} type
     */
    const createOperationHandler = ({ type }) =>
      operationsService.initForNew(type)

    /**
     * Update an existing operation
     * @param {String} uuid
     * @returns {*}
     */
    const updateOperationHandler = ({ uuid }) =>
      operationsService.initForUUID(uuid)

    /**
     * Delete an existing operation
     * @param {String} name
     * @param {String} uuid}
     */
    const deleteOperationHandler = ({ name, uuid }) =>
      DialogProgrammatic.prompt({
        title: `Delete Operation`,
        message: `
          <p>
            Are you sure you want to delete ${name}?
          </p>
          <small><strong>Please type DELETE to confirm</strong></small>
        `,
        inputAttrs: {
          pattern: 'DELETE',
        },
        trapFocus: true,
        type: 'danger',
        hasIcon: true,
        size: 'small',
        confirmText: 'Yes',
        cancelText: 'No',
        onConfirm: () => operationsService.deleteByUUID(uuid),
      })

    /**
     * The current number of operations
     * @type {ComputedRef<T>}
     */
    const operationsCount = computed(
      () => props.service.inboundOperationsCount.value,
    )

    /**
     * The limit for the maximum number of operation instances that can be configured
     * @type {ComputedRef<unknown>}
     */
    const operationsLimit = computed(() => {
      const quotaEntry =
        props.service.getFirstQuotaByType('max-operations').value
      return (quotaEntry && quotaEntry.limit) || Infinity
    })

    /**
     * Determine if an operation can be created
     * @type {ComputedRef<unknown>}
     */
    const canCreateOperation = computed(
      () => operationsCount.value < operationsLimit.value,
    )

    return {
      createOperationHandler,
      updateOperationHandler,
      deleteOperationHandler,
      operationsService,
      operationsCount,
      operationsLimit,
      canCreateOperation,
    }
  },
})
</script>
